/**
 * Shared UI validation for email, phone, and date fields.
 * Use for real-time feedback and submit prevention.
 * Source of truth for web and mobile: synced to mobile-app via scripts/sync-shared.js.
 */

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[\d\s+\-().]{7,25}$/;
const PHONE_MIN_DIGITS = 7;

function countDigits(s) {
  return (s.match(/\d/g) || []).length;
}

/**
 * @param {string} value
 * @returns {{ valid: boolean, message?: string }}
 */
export function validateEmail(value) {
  if (value === undefined || value === null) return { valid: false, message: 'Email is required' };
  const s = String(value).trim();
  if (s.length === 0) return { valid: false, message: 'Email is required' };
  if (s.length > 254) return { valid: false, message: 'Invalid email format' };
  if (!EMAIL_REGEX.test(s)) return { valid: false, message: 'Enter a valid email address' };
  return { valid: true };
}

/**
 * Optional email (empty is valid).
 * @param {string} value
 * @returns {{ valid: boolean, message?: string }}
 */
export function validateOptionalEmail(value) {
  if (value === undefined || value === null) return { valid: true };
  const s = String(value).trim();
  if (s.length === 0) return { valid: true };
  if (s.length > 254) return { valid: false, message: 'Invalid email format' };
  if (!EMAIL_REGEX.test(s)) return { valid: false, message: 'Enter a valid email address' };
  return { valid: true };
}

/**
 * @param {string} value
 * @returns {{ valid: boolean, message?: string }}
 */
export function validatePhone(value) {
  if (value === undefined || value === null) return { valid: false, message: 'Phone is required' };
  const s = String(value).trim();
  if (s.length === 0) return { valid: false, message: 'Phone is required' };
  if (s.length > 25) return { valid: false, message: 'Invalid phone format' };
  if (!PHONE_REGEX.test(s) || countDigits(s) < PHONE_MIN_DIGITS) {
    return { valid: false, message: 'Use digits, spaces, + - ( ) only; at least 7 digits' };
  }
  return { valid: true };
}

/**
 * Optional phone (empty is valid).
 * @param {string} value
 * @returns {{ valid: boolean, message?: string }}
 */
export function validateOptionalPhone(value) {
  if (value === undefined || value === null) return { valid: true };
  const s = String(value).trim();
  if (s.length === 0) return { valid: true };
  const digits = countDigits(s);
  // No digits => no phone (covers invisible unicode, legacy masks, “looks empty” DB values).
  if (digits === 0) return { valid: true };
  if (s.length > 25) return { valid: false, message: 'Invalid phone format' };
  if (!PHONE_REGEX.test(s) || digits < PHONE_MIN_DIGITS) {
    return { valid: false, message: 'Use digits, spaces, + - ( ) only; at least 7 digits' };
  }
  return { valid: true };
}

/**
 * @param {string} value - ISO date (YYYY-MM-DD) or parseable date string
 * @returns {{ valid: boolean, message?: string }}
 */
export function validateDate(value) {
  if (value === undefined || value === null) return { valid: false, message: 'Date is required' };
  const s = String(value).trim();
  if (s.length === 0) return { valid: false, message: 'Date is required' };
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return { valid: false, message: 'Enter a valid date' };
  const year = d.getFullYear();
  if (year < 1900 || year > 2100) return { valid: false, message: 'Date must be between 1900 and 2100' };
  return { valid: true };
}

/**
 * Optional date (empty is valid).
 * @param {string} value
 * @returns {{ valid: boolean, message?: string }}
 */
export function validateOptionalDate(value) {
  if (value === undefined || value === null) return { valid: true };
  const s = String(value).trim();
  if (s.length === 0) return { valid: true };
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return { valid: false, message: 'Enter a valid date' };
  const year = d.getFullYear();
  if (year < 1900 || year > 2100) return { valid: false, message: 'Date must be between 1900 and 2100' };
  return { valid: true };
}

/**
 * For datetime-local or combined date+time strings.
 * @param {string} value
 * @returns {{ valid: boolean, message?: string }}
 */
export function validateDateTime(value) {
  if (value === undefined || value === null) return { valid: false, message: 'Date and time are required' };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { valid: false, message: 'Enter a valid date and time' };
  const year = d.getFullYear();
  if (year < 1900 || year > 2100) return { valid: false, message: 'Date must be between 1900 and 2100' };
  return { valid: true };
}

/**
 * Validates that an end date/time is not before a start date/time.
 * Both values should be parseable by the Date constructor (e.g. YYYY-MM-DD or YYYY-MM-DDTHH:mm).
 * Use options.granularity = 'day' when comparing dates only (ignores time in the error message).
 *
 * @param {string} startValue
 * @param {string} endValue
 * @param {{ allowEqual?: boolean, granularity?: 'day' | 'time' }} [options]
 * @returns {{ valid: boolean, message?: string }}
 */
export function validateStartEndRange(startValue, endValue, options = {}) {
  const { allowEqual = true, granularity = 'time' } = options;

  if (startValue == null || endValue == null || String(startValue).trim() === '' || String(endValue).trim() === '') {
    return { valid: false, message: granularity === 'day' ? 'Start date and end date are required.' : 'Start and end time are required.' };
  }

  const start = new Date(startValue);
  const end = new Date(endValue);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return {
      valid: false,
      message: granularity === 'day' ? 'Enter valid start and end dates.' : 'Enter valid start and end date/time.',
    };
  }

  const startMs = start.getTime();
  const endMs = end.getTime();

  if (allowEqual ? endMs < startMs : endMs <= startMs) {
    return {
      valid: false,
      message: granularity === 'day'
        ? 'End date must be on or after start date.'
        : 'End time must be on or after start time.',
    };
  }

  return { valid: true };
}

/** Subdomain-safe: lowercase letters, numbers, hyphen; 2–63 chars; cannot start or end with hyphen */
const SITE_NAME_REGEX = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * Validates site_name for subdomain-based tenancy (e.g. acme in acme.localhost:3000).
 * @param {string} value
 * @returns {{ valid: boolean, message?: string }}
 */
export function validateSiteName(value) {
  if (value === undefined || value === null) return { valid: false, message: 'Site name is required' };
  const s = String(value).trim().toLowerCase();
  if (s.length === 0) return { valid: false, message: 'Site name is required' };
  if (s.length < 2) return { valid: false, message: 'Site name must be at least 2 characters' };
  if (s.length > 63) return { valid: false, message: 'Site name must not exceed 63 characters' };
  if (!SITE_NAME_REGEX.test(s)) {
    return { valid: false, message: 'Use only lowercase letters, numbers, and hyphens (cannot start or end with a hyphen)' };
  }
  return { valid: true };
}

/**
 * Format collected field errors for alert/banner display after submit.
 * Single error: plain message. Multiple: one bullet per line (pre-wrap friendly).
 * @param {string[]|string|null|undefined} errors
 * @returns {string}
 */
export function formatValidationErrorList(errors) {
  const list = (Array.isArray(errors) ? errors : [errors])
    .map((e) => (e == null ? '' : String(e).trim()))
    .filter(Boolean);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  return list.map((e) => `• ${e}`).join('\n');
}
